import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { advisorUrlTransform } from "@sill/shared/lib/safe-markdown-url";

import { cn } from "../../lib/utils";

// Minimal, on-brand markdown styling for LLM output. We deliberately don't
// pull in @tailwindcss/typography — the advisor produces a constrained
// palette (headings, lists, tables, inline code, emphasis) and these rules
// keep the bubble legible without dragging a whole prose stylesheet onto
// every page.
const components: Components = {
  p: ({ children }) => (
    <p className="text-[15px] font-medium leading-6 text-foreground">{children}</p>
  ),
  h1: ({ children }) => (
    <h1 className="mt-2 text-lg font-semibold text-foreground">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-2 text-base font-semibold text-foreground">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-2 text-[15px] font-semibold text-foreground">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-1 text-[14px] font-semibold text-foreground">{children}</h4>
  ),
  ul: ({ children }) => (
    <ul className="list-disc space-y-1 pl-5 text-[15px] font-medium leading-6 text-foreground marker:text-muted-foreground">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal space-y-1 pl-5 text-[15px] font-medium leading-6 text-foreground marker:text-muted-foreground">
      {children}
    </ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  // Never render an image (SEC-03): a browser fetches <img src> with no
  // click, so model output must not be able to make it request a URL.
  img: () => null,
  // A link whose href the urlTransform dropped renders as plain text.
  a: ({ href, children }) =>
    href ? (
      <a
        className="text-primary underline underline-offset-2 hover:text-primary/80"
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
      </a>
    ) : (
      <>{children}</>
    ),
  code: ({ children, className: codeClass }) => {
    const isBlock = (codeClass ?? "").includes("language-");
    if (isBlock) {
      return (
        <code className="block whitespace-pre rounded-xl border border-border/60 bg-surface-raised px-3 py-2 text-[13px] leading-5 font-mono text-foreground">
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-muted px-1 py-0.5 text-[13px] font-mono text-foreground">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-xl border border-border/60 bg-surface-raised p-3 text-[13px] leading-5">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 text-[15px] font-medium leading-6 text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-border/60" />,
  table: ({ children }) => (
    <div className="overflow-x-auto rounded-xl border border-border/60">
      <table className="w-full border-collapse text-[14px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-muted/60 text-left">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-border/60 last:border-b-0">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 font-semibold text-foreground">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-foreground">{children}</td>
  ),
};

export function AdvisorMarkdown({
  children,
  className,
  suppressTables = false,
}: {
  children: string;
  className?: string;
  suppressTables?: boolean;
}) {
  const content = suppressTables ? stripMarkdownTables(children) : children;

  return (
    <div className={cn("space-y-2", className)} data-slot="advisor-markdown">
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        urlTransform={advisorUrlTransform}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function stripMarkdownTables(markdown: string) {
  const lines = markdown.split(/\r?\n/);
  const kept: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    if (!startsMarkdownTable(lines[index], lines[index + 1])) {
      kept.push(lines[index]);
      continue;
    }

    removeHeadingBeforeTable(kept);
    index += 1;

    while (index + 1 < lines.length && isMarkdownTableRow(lines[index + 1])) {
      index += 1;
    }

    if (kept.length > 0 && kept.at(-1)?.trim() !== "") {
      kept.push("");
    }
  }

  return kept.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function startsMarkdownTable(line: string | undefined, nextLine: string | undefined) {
  return isMarkdownTableRow(line) && isMarkdownDivider(nextLine);
}

function isMarkdownTableRow(line: string | undefined) {
  const trimmed = line?.trim() ?? "";
  return trimmed.includes("|") && trimmed !== "";
}

function isMarkdownDivider(line: string | undefined) {
  const trimmed = line?.trim() ?? "";
  return /^\|?(?:\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?$/.test(trimmed);
}

function removeHeadingBeforeTable(lines: string[]) {
  while (lines.length > 0 && lines.at(-1)?.trim() === "") {
    lines.pop();
  }

  const last = lines.at(-1)?.trim() ?? "";
  if (/^#{1,6}\s+/.test(last)) {
    lines.pop();
  }
}
